"use client";

import { AnimatePresence, motion } from "framer-motion";
import { debounce } from "lodash";
import { Search } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import AddressEmpty from "@/assets/svg/address-empty.svg";
import CircleX from "@/assets/svg/coupon-remove.svg";
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCurrentAreaInClient, getCurrentLanguageInClient } from "@/lib/utils.client";
export interface AddressFinderProps {
  currentAreaCode?: string;
  className?: string;
  onSelect: (place: google.maps.places.PlaceResult) => void;
  onManual: (value: string) => void;
  onChange?: (value: string) => void; // 新增：同步输入值变化
}
const dinProRegular = "font-['DINPro-Regular']";
const AddressFinder: React.FC<AddressFinderProps> = ({
  currentAreaCode,
  className,
  onSelect,
  onManual,
  onChange
}) => {
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  const [value, setValue] = useState("");
  const [mobileInputValue, setMobileInputValue] = useState(""); // 移动端弹窗内的输入值
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileDialog, setShowMobileDialog] = useState(false);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1); // 当前选中的索引
  const [isAnimating, setIsAnimating] = useState(false); // 动画状态
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  // 语言和地区设置
  const lang = getCurrentLanguageInClient();
  const region = currentAreaCode || getCurrentAreaInClient();

  // 获取地址建议
  const getAddressSuggestions = useCallback(async (inputValue: string) => {
    if (!inputValue.trim() || typeof google === "undefined") {
      setSuggestions([]);
      return;
    }
    setIsLoading(true);
    try {
      const service = new google.maps.places.AutocompleteService();
      const request = {
        input: inputValue,
        types: ["address"],
        ...(region ? {
          componentRestrictions: {
            country: region.toUpperCase()
          }
        } : {})
      };
      service.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      });
    } catch (error) {
      setIsLoading(false);
      console.error("Error getting place predictions:", error);
      setSuggestions([]);
    }
  }, [region]);

  // 使用 lodash debounce 创建防抖版本的获取建议函数
  const debouncedGetSuggestions = useCallback(debounce((inputValue_0: string) => {
    getAddressSuggestions(inputValue_0);
  }, 300), []);

  // 初始化Google Maps API
  useEffect(() => {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error("Google Maps API key is not configured");
      return;
    }

    // 检查是否已经加载
    if (typeof google !== "undefined" && google.maps && google.maps.places) {
      return;
    }

    // 动态加载Google Maps API
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry,geocoding&language=${lang}${region ? `&region=${region}` : ""}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Google Maps API 加载成功
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps API");
    };
    document.head.appendChild(script);
  }, [lang, region]);

  // 点击外部关闭dropdown
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      const target = e.target as HTMLElement;
      if (!wrapperRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // 计算输入框的位置，用于 Portal 定位
  const [inputPosition, setInputPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });

  // 更新输入框位置
  const updateInputPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setInputPosition({
        top: rect.top + rect.height + 2,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
  }, []);

  // 监听输入框位置变化
  useEffect(() => {
    if (showDropdown) {
      updateInputPosition();
      // 监听窗口大小变化和滚动
      window.addEventListener("resize", updateInputPosition);
      window.addEventListener("scroll", updateInputPosition);

      // 使用更频繁的位置更新，减少延迟
      const interval = setInterval(updateInputPosition, 16); // 约60fps

      return () => {
        window.removeEventListener("resize", updateInputPosition);
        window.removeEventListener("scroll", updateInputPosition);
        clearInterval(interval);
      };
    }
  }, [showDropdown, updateInputPosition]);

  // 从地址描述中提取街道信息（作为备选方案）
  const extractStreetFromDescription = useCallback((description: string) => {
    // 尝试从 description 中提取街道信息
    // 通常 description 格式为 "123 Main Street, City, State, Country"
    const parts = description.split(",");
    if (parts.length > 0) {
      const firstPart = parts[0].trim();
      // 检查是否包含数字（门牌号）和街道名
      if (/\d/.test(firstPart) && firstPart.length > 5) {
        return firstPart;
      }
    }
    return "";
  }, []);

  // 解析地址组件，将 street_number 和 route 用空格拼接
  const parseAddressComponents = useCallback((components: google.maps.GeocoderAddressComponent[]) => {
    let streetNumber = "";
    let route = "";
    components.forEach(component => {
      const types = component.types;
      if (types.includes("street_number")) {
        streetNumber = component.long_name || component.short_name || "";
      }
      if (types.includes("route")) {
        route = component.long_name || component.short_name || "";
      }
    });

    // 将 street_number 和 route 用空格拼接
    return [streetNumber, route].filter(Boolean).join(" ");
  }, []);

  // 统一的地址选择和回填处理函数
  const handleAddressSelection = useCallback((place: google.maps.places.AutocompletePrediction, streetAddress?: string, addressComponents?: google.maps.GeocoderAddressComponent[], geometry?: google.maps.GeocoderGeometry | google.maps.places.PlaceGeometry) => {
    // 优先使用解析出的街道地址，否则尝试从描述中提取
    const finalStreetAddress = streetAddress || extractStreetFromDescription(place.description || "");

    // 创建 PlaceResult 对象
    const placeResult: google.maps.places.PlaceResult = {
      place_id: place.place_id || "",
      formatted_address: finalStreetAddress || place.description || "",
      name: finalStreetAddress || place.description || "",
      address_components: addressComponents || [],
      geometry: geometry
    };

    // 更新输入框值
    setValue(finalStreetAddress || place.description || "");

    // 传递给父组件
    onSelect(placeResult);

    // 关闭下拉框和重置状态
    setShowDropdown(false);
    setSelectedIndex(-1);

    // 如果是移动端，关闭对话框并清空弹窗内的输入
    if (isMobile) {
      setShowMobileDialog(false);
      setMobileInputValue("");
    }
  }, [onSelect, extractStreetFromDescription, isMobile]);

  // 备选方案：使用 Places API
  const tryPlacesAPI = useCallback((place_0: google.maps.places.AutocompletePrediction) => {
    if (place_0.place_id && typeof google !== "undefined" && google.maps && google.maps.places) {
      try {
        // 确保 wrapperRef.current 存在且是有效的 DOM 元素
        if (!wrapperRef.current) {
          console.error("Wrapper ref not available");
          handleAddressSelection(place_0);
          return;
        }
        const service_0 = new google.maps.places.PlacesService(wrapperRef.current as HTMLDivElement);

        // 根据文档，fields 参数必须包含所有需要的字段
        const request_0: google.maps.places.PlaceDetailsRequest = {
          placeId: place_0.place_id,
          fields: ["address_components", "formatted_address", "name", "geometry", "types", "place_id", "url", "website", "formatted_phone_number", "international_phone_number", "rating", "reviews", "opening_hours", "price_level", "photos"]
        };
        service_0.getDetails(request_0, (detailedPlace, status_0) => {
          if (status_0 === google.maps.places.PlacesServiceStatus.OK && detailedPlace) {
            // 解析地址组件
            if (detailedPlace.address_components && detailedPlace.address_components.length > 0) {
              // 解析街道地址
              const streetAddress_0 = parseAddressComponents(detailedPlace.address_components);
              handleAddressSelection(place_0, streetAddress_0, detailedPlace.address_components, detailedPlace.geometry);
              return;
            } else {
              // 如果没有地址组件，尝试使用 Geocoding API 作为备选
              const geocoder = new google.maps.Geocoder();
              geocoder.geocode({
                address: place_0.description
              }, (results, geocodeStatus) => {
                if (geocodeStatus === google.maps.GeocoderStatus.OK && results && results.length > 0) {
                  const result = results[0];
                  const streetAddress_1 = result.address_components ? parseAddressComponents(result.address_components) : "";
                  handleAddressSelection(place_0, streetAddress_1, result.address_components, result.geometry);
                } else {
                  // 最后使用基本信息
                  handleAddressSelection(place_0);
                }
              });
              return; // 不要继续执行下面的代码
            }
          } else {
            // 如果 Places API 也失败，使用基本信息
            handleAddressSelection(place_0);
          }
        });
      } catch (_error) {
        // 出错时使用基本信息
        handleAddressSelection(place_0);
      }
    } else {
      // 没有 place_id 时直接使用基本信息
      handleAddressSelection(place_0);
    }
  }, [handleAddressSelection, parseAddressComponents]);

  // 选择地址 - 必须在这里定义，因为 handleKeyDown 会引用它
  const handlePlaceSelect = useCallback(async (place_1: google.maps.places.AutocompletePrediction) => {
    // 检查 API 是否完全加载
    if (typeof google === "undefined" || !google.maps) {
      console.error("Google Maps API not loaded");
      handleAddressSelection(place_1);
      return;
    }

    // 优先使用 Geocoding API，因为它更稳定可靠
    if (google.maps.Geocoder) {
      try {
        const geocoder_0 = new google.maps.Geocoder();
        const address = place_1.description || "";
        geocoder_0.geocode({
          address
        }, (results_0, status_1) => {
          if (status_1 === google.maps.GeocoderStatus.OK && results_0 && results_0.length > 0) {
            const result_0 = results_0[0];
            const streetAddress_2 = result_0.address_components ? parseAddressComponents(result_0.address_components) : "";
            handleAddressSelection(place_1, streetAddress_2, result_0.address_components, result_0.geometry);
          } else {
            // 如果 Geocoding 失败，尝试使用 Places API
            tryPlacesAPI(place_1);
          }
        });
      } catch (_error_0) {
        // 出错时尝试 Places API
        tryPlacesAPI(place_1);
      }
    } else {
      // 如果没有 Geocoding API，尝试使用 Places API
      tryPlacesAPI(place_1);
    }
  }, [handleAddressSelection, parseAddressComponents, tryPlacesAPI]);

  // 键盘导航处理 - 现在可以安全地引用 handlePlaceSelect
  const handleKeyDown = useCallback((e_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;
    switch (e_0.key) {
      case "ArrowDown":
        e_0.preventDefault();
        setSelectedIndex(prev_0 => prev_0 < suggestions.length - 1 ? prev_0 + 1 : 0);
        break;
      case "ArrowUp":
        e_0.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
        break;
      case "Enter":
        e_0.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          const selectedPlace = suggestions[selectedIndex];
          // 调用 handlePlaceSelect 而不是直接调用 onSelect
          handlePlaceSelect(selectedPlace);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showDropdown, suggestions, selectedIndex, handlePlaceSelect]);

  // 手动输入
  const handleManualInput = useCallback(() => {
    setShowDropdown(false);
    setSelectedIndex(-1);
    onManual(value);

    // 如果是移动端，关闭对话框并清空弹窗内的输入
    if (isMobile) {
      setShowMobileDialog(false);
      setMobileInputValue("");
    }
  }, [value, onManual, isMobile]);

  // 重置选中状态和动画
  useEffect(() => {
    if (!showDropdown) {
      setSelectedIndex(-1);
      setIsAnimating(false);
    } else {
      // 展开时触发动画
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    }
  }, [showDropdown]);

  // 移动端对话框打开时自动聚焦输入框
  useEffect(() => {
    if (showMobileDialog && mobileInputRef.current) {
      // 延迟聚焦，确保对话框完全打开
      setTimeout(() => {
        mobileInputRef.current?.focus();
      }, 100);
    }
  }, [showMobileDialog]);

  // 渲染地址建议列表的通用函数
  const renderSuggestions = () => <>
			{/* 加载状态或提示 */}
			{isLoading ? <div className={`${dinProRegular} p-4 text-center text-gray-600`}>
					<PopLoading />
				</div> : suggestions.length > 0 ? (/* 真实的地址建议 */
    suggestions.map((place_2, index) => <button key={place_2.place_id || index} type="button" className={`w-full text-left cursor-pointer border-b border-gray-100 p-3 transition-colors last:border-b-0 ${index === selectedIndex ? "border-gray-300 bg-gray-100" : "hover:bg-[#f1f3f4]"}`} onClick={() => {
      handlePlaceSelect(place_2);
    }} onMouseDown={() => {
      // 移除 preventDefault，让事件正常传播
      handlePlaceSelect(place_2);
    }} onMouseUp={() => {
      // 在 mouse up 时也尝试处理点击
      handlePlaceSelect(place_2);
    }} onMouseEnter={() => {
      setSelectedIndex(index);
    }}
    // 添加触摸事件支持
    onTouchStart={() => {}} onTouchEnd={() => {
      handlePlaceSelect(place_2);
    }} aria-label={`Select address: ${place_2.description || "Unknown address"}`}>
						<div className={`${dinProRegular} text-sm text-gray-600`}>
							{place_2.description || "Unknown address"}
						</div>
					</button>)) : <div className={`${dinProRegular} p-4 text-center text-gray-600`}>
					{/* 空白内容 */}
				</div>}

			{/* 手动输入选项 - 只在PC端显示 */}
			{!isMobile && <button type="button" className={`w-full text-left cursor-pointer p-3 transition-colors ${selectedIndex === suggestions.length ? "bg-gray-100" : "hover:bg-[#f1f3f4]"}`} onClick={() => {
      handleManualInput();
    }} onMouseDown={() => {
      handleManualInput();
    }} onMouseEnter={() => {
      setSelectedIndex(suggestions.length);
    }} onTouchEnd={() => {
      handleManualInput();
    }} aria-label="Enter address manually">
					<div className={`${dinProRegular} flex items-center justify-center gap-2 text-sm`}>
						<span className="text-gray-600">
							{t("order.cannotFindAddress", {
            defaultValue: "Cannot find your address?"
          })}
						</span>
						<span className="text-black underline">
							{t("order.enterManually", {
            defaultValue: "Enter it manually."
          })}
						</span>
					</div>
				</button>}
		</>;
  return <div className={`${className} relative`} ref={wrapperRef} style={{
    overflow: "visible"
  }}>
			{isMobile ?
    // 移动端：使用按钮触发对话框
    <>
					<div>
						<Input ref={inputRef} value={value} onChange={() => {}} // 禁用输入
        onFocus={() => setShowMobileDialog(true)} onClick={() => setShowMobileDialog(true)} name="addressFinder" placeholder={t("order.addressPlaceholderRestricted")} autoComplete="off" readOnly className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] cursor-pointer`} />
					</div>

					{/* Enter it manually button */}
					<div className="mt-2 text-right">
						<button type="button" className={`${dinProRegular} text-xs text-gray-700 underline`} onClick={() => onManual(value)}>
							{t("order.enterManually", {
            defaultValue: "Enter it manually."
          })}
						</button>
					</div>

					{/* 移动端弹窗 */}
					<AnimatePresence>
						{showMobileDialog && <>
								{/* 背景遮罩 */}
								<motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowMobileDialog(false)} />

								{/* 弹窗内容 */}
								<motion.div initial={{
            y: -100,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: -100,
            opacity: 0
          }} transition={{
            type: "spring",
            damping: 25,
            stiffness: 200
          }} className="fixed top-8 left-1/2 transform -translate-x-1/2 w-full max-w-[calc(100vw-2rem)] bg-white border border-black z-50 flex flex-col">
									{/* 固定的输入框区域 */}
									<div className="relative shrink-0">
										<Input ref={mobileInputRef} value={mobileInputValue} onChange={e_1 => {
                const newValue = e_1.target.value;
                setMobileInputValue(newValue);
                setSelectedIndex(-1); // 重置选中状态

                // 使用 lodash debounce 防抖处理
                debouncedGetSuggestions(newValue);
              }} onFocus={() => {
                setShowDropdown(true);
                setSelectedIndex(-1);
              }} onKeyDown={handleKeyDown} name="addressFinderMobile" placeholder={t("order.addressPlaceholderRestricted")} autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9] rounded-none border-0 pl-10 pr-12 h-10`} />

										{/* 左侧放大镜图标 */}
										<div className="absolute left-3 top-1/2 transform -translate-y-1/2">
											<Search className="w-4 h-4 text-gray-400" />
										</div>

										{/* 清空按钮 - 只在有内容时显示 */}
										{mobileInputValue && <button type="button" onClick={() => {
                setMobileInputValue("");
                setSuggestions([]);
                setShowDropdown(false);
                setSelectedIndex(-1);
              }} className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600">
												<CircleX className="w-4 h-4" />
											</button>}
									</div>

									{/* 地址建议列表区域 - 始终显示 */}
									<div className="bg-white border-t border-black">
										{showDropdown && suggestions.length > 0 ? <>
												{renderSuggestions()}
												{/* 移动端手动输入按钮 */}
												<button type="button" className="w-full text-left cursor-pointer p-3 transition-colors hover:bg-[#f1f3f4] border-gray-100" onClick={() => {
                  handleManualInput();
                }} aria-label="Enter address manually">
													<div className={`${dinProRegular} flex items-center justify-center gap-2 text-sm`}>
														<span className="text-black">
															{t("order.cannotFindAddress", {
                        defaultValue: "Cannot find your address?"
                      })}
														</span>
														<span className="text-black underline">
															{t("order.enterManually", {
                        defaultValue: "Enter it manually."
                      })}
														</span>
													</div>
												</button>
											</> : <div className={`${dinProRegular} p-4 text-center `}>
												<div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
													<AddressEmpty className="w-16 h-16" />
												</div>
												<p className="text-sm">
													{t("order.cannotFindAddress", {
                    defaultValue: "Cannot find your address?"
                  })}{" "}
													<button type="button" className="text-sm text-black underline" onClick={handleManualInput}>
														{t("order.enterManually", {
                      defaultValue: "Enter it manually."
                    })}
													</button>
												</p>
											</div>}
									</div>
								</motion.div>
							</>}
					</AnimatePresence>
				</> :
    // 桌面端：正常的输入框交互
    <>
					<div>
						<Input ref={inputRef} value={value} onChange={e_2 => {
          const newValue_0 = e_2.target.value;
          setValue(newValue_0);
          setSelectedIndex(-1); // 重置选中状态

          // 使用 lodash debounce 防抖处理
          debouncedGetSuggestions(newValue_0);
          onChange?.(newValue_0); // 调用 onChange 回调
        }} onFocus={() => {
          setShowDropdown(true);
          setSelectedIndex(-1);
        }} onKeyDown={handleKeyDown} name="addressFinder" placeholder={t("order.addressPlaceholderRestricted")} autoComplete="off" className={`${dinProRegular} w-full text-black shadow-none bg-[#F9F9F9]`} />
					</div>

					{/* Enter it manually button */}
					<div className="mt-2 text-right">
						<button type="button" className={`${dinProRegular} text-xs text-gray-700 underline`} onClick={() => onManual(value)}>
							{t("order.enterManually", {
            defaultValue: "Enter it manually."
          })}
						</button>
					</div>

					{/* 桌面端地址下拉列表 - 使用 Portal 渲染到 body 下，基于父组件定位 */}
					{showDropdown && <>
							{/* Portal 版本 - 基于父组件定位 */}
							{typeof document !== "undefined" && createPortal(<div className="fixed border border-black bg-white shadow-lg" style={{
          top: inputPosition.top,
          // 输入框下方 + 2px 间距
          left: inputPosition.left,
          // 输入框左边
          width: inputPosition.width,
          // 与输入框同宽
          maxHeight: isAnimating ? 300 : 0,
          // 设置合理的最大高度
          zIndex: 9999,
          // 使用更高的 z-index
          transform: "translateZ(0)",
          // 创建新的层叠上下文
          willChange: "transform",
          // 优化性能
          pointerEvents: "auto",
          // 确保鼠标事件能正常工作
          transition: "max-height 0.2s ease-in-out, opacity 0.2s ease-in-out",
          overflowY: "auto" // 允许垂直滚动
        }}>
										{renderSuggestions()}
									</div>, document.body)}
						</>}
				</>}
		</div>;
};
export default AddressFinder;